import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { FaSearch, FaEdit, FaTrash, FaChevronLeft, FaChevronRight, FaPlus } from "react-icons/fa"
import { toast, Toaster } from "react-hot-toast"

// CSS animation definitions
const globalStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    0% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideIn {
    0% { opacity: 0; transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`

export default function ProductGridPage() {
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [categoriesLoading, setCategoriesLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [confirmDelete, setConfirmDelete] = useState({ open: false, productId: null })
  const [deleting, setDeleting] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    totalPages: 1,
    totalProducts: 0,
  })
  const [selectedCategory, setSelectedCategory] = useState("All Categories")
  const [selectedStatus, setSelectedStatus] = useState("All Status")

  // Check screen size on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      setIsSmallMobile(window.innerWidth <= 480)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true)
      const response = await fetch("https://api.alburaqscents.com/v1/category")
      const result = await response.json()

      if (result.success) {
        setCategories(result.categories)
      } else {
        toast.error("Failed to load categories")
      }
    } catch (error) {
      console.error("Error fetching categories:", error)
      toast.error("Network error while loading categories")
    } finally {
      setCategoriesLoading(false)
    }
  }

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories()
  }, [])

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true)
      let url = `https://api.alburaqscents.com/v1/product?page=${pagination.page}&limit=${pagination.limit}`

      if (searchTerm) url += `&search=${searchTerm}`
      if (selectedCategory !== "All Categories") {
        url += `&category=${selectedCategory.toLowerCase()}`
      }

      // Handle status filtering
      if (selectedStatus !== "All Status") {
        if (selectedStatus === "Active") {
          url += `&status=active`
        } else if (selectedStatus === "Inactive") {
          url += `&status=inactive`
        }
        // For "Out of Stock", we'll filter on the frontend after getting active products
        if (selectedStatus === "Out of Stock") {
          url += `&status=active` // Get active products first, then filter by stock
        }
      }

      const response = await fetch(url)
      const result = await response.json()

      if (result.success) {
        const processedProducts = result.data.map((product) => {
          const largestSize = product.sizes.reduce((max, size) => (size.ml > max.ml ? size : max), { ml: 0, price: 0 })
          const totalStock = product.sizes.reduce((sum, size) => sum + size.stock, 0)

          // Determine status display based on product status and stock
          let statusDisplay
          if (product.status === "active") {
            statusDisplay = totalStock > 0 ? "active" : "out of stock"
          } else {
            statusDisplay = "inactive"
          }

          return {
            ...product,
            displayPrice: largestSize.price,
            totalStock,
            statusDisplay,
          }
        })

        // Additional frontend filtering for "Out of Stock" only
        let filteredProducts = processedProducts
        if (selectedStatus === "Out of Stock") {
          filteredProducts = processedProducts.filter((product) => product.statusDisplay === "out of stock")
        }

        setProducts(filteredProducts)
        setPagination({
          page: result.page,
          limit: result.limit,
          totalPages: result.totalPages,
          totalProducts: selectedStatus === "Out of Stock" ? filteredProducts.length : result.totalProducts,
        })
      } else {
        toast.error(result.message || "Failed to load products")
      }
    } catch (error) {
      console.error("Error fetching products:", error)
      toast.error("Network error while loading products")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [pagination.page, searchTerm, selectedCategory, selectedStatus])

  const handleViewProduct = (productId) => {
    navigate(`/products/details/${productId}`)
  }

  const handleEditProduct = (productId) => {
    navigate(`/products/edit/${productId}`)
  }

  const handleDeleteProduct = async (productId) => {
    setConfirmDelete({ open: true, productId })
  }

  const confirmDeletion = async () => {
    const productId = confirmDelete.productId
    setDeleting(true)
    try {
      const response = await fetch(`https://api.alburaqscents.com/v1/product/${productId}`, {
        method: "DELETE",
      })
      const result = await response.json()
      if (result.success) {
        toast.success("Product deleted successfully")
        fetchProducts()
      } else {
        toast.error(result.message || "Failed to delete product")
      }
    } catch (error) {
      toast.error("Network error while deleting product")
    } finally {
      setDeleting(false)
      setConfirmDelete({ open: false, productId: null })
    }
  }

  const handleAddProduct = () => {
    navigate("/products/add")
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }))
    }
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const renderPaginationButtons = () => {
    const buttons = []
    const maxVisibleButtons = 5
    let startPage = 1
    let endPage = pagination.totalPages

    if (pagination.totalPages > maxVisibleButtons) {
      const half = Math.floor(maxVisibleButtons / 2)
      if (pagination.page <= half) {
        endPage = maxVisibleButtons
      } else if (pagination.page + half >= pagination.totalPages) {
        startPage = pagination.totalPages - maxVisibleButtons + 1
      } else {
        startPage = pagination.page - half
        endPage = pagination.page + half
      }
    }

    if (startPage > 1) {
      buttons.push(
        <button key={1} style={styles.paginationButton} onClick={() => handlePageChange(1)}>
          1
        </button>,
      )
      if (startPage > 2) {
        buttons.push(
          <span key="start-ellipsis" style={styles.paginationText}>
            ...
          </span>,
        )
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          style={{
            ...styles.paginationButton,
            ...(i === pagination.page && styles.paginationButtonActive),
          }}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>,
      )
    }

    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) {
        buttons.push(
          <span key="end-ellipsis" style={styles.paginationText}>
            ...
          </span>,
        )
      }
      buttons.push(
        <button
          key={pagination.totalPages}
          style={styles.paginationButton}
          onClick={() => handlePageChange(pagination.totalPages)}
        >
          {pagination.totalPages}
        </button>,
      )
    }

    return buttons
  }

  const getStatusStyle = (status) => {
    switch (status) {
      case "active":
        return styles.statusActive
      case "inactive":
        return styles.statusInactive
      case "out of stock":
        return styles.outOfStock
      default:
        return {}
    }
  }

  const styles = {
    mainContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      minHeight: "100vh",
      backgroundColor: "#E5D0A5",
      fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      padding: isSmallMobile ? "20px" : isMobile ? "35px" : "45px",
      boxSizing: "border-box",
      width: "100%",
      animation: "fadeIn 0.6s ease-in-out",
    },
    contentWrapper: {
      width: "100%",
      backgroundColor: "transparent",
      padding: "0px",
      boxSizing: "border-box",
      borderRadius: "12px",
    },
    headerSection: {
      padding: "0",
      marginBottom: "35px",
      boxSizing: "border-box",
      width: "100%",
    },
    pageTitle: {
      fontSize: isSmallMobile ? "28px" : isMobile ? "32px" : "38px",
      fontWeight: "700",
      color: "#000",
      marginBottom: "8px",
      textAlign: "left",
      letterSpacing: "-0.5px",
      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    breadcrumbs: {
      fontSize: isSmallMobile ? "13px" : "15px",
      color: "#666",
      marginBottom: "35px",
      textAlign: "left",
      fontWeight: "500",
    },
    mainContentSection: {
      backgroundColor: "#000",
      borderRadius: "15px",
      padding: isSmallMobile ? "20px" : "30px",
      margin: "0",
      boxSizing: "border-box",
      width: "100%",
      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      border: "1px solid #222",
    },
    searchAndAddSection: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "25px",
      flexWrap: "wrap",
      marginBottom: "40px",
      padding: "0",
      flexDirection: isSmallMobile ? "column" : "row",
    },
    searchBarContainer: {
      flexGrow: 1,
      minWidth: "220px",
      maxWidth: "450px",
      position: "relative",
      display: "flex",
      alignItems: "center",
      backgroundColor: "rgba(212, 175, 55, 0.1)",
      borderRadius: "30px",
      border: "2px solid #D4AF37",
      height: "42px",
      padding: "8px 16px",
      boxSizing: "border-box",
      width: isSmallMobile ? "100%" : "auto",
      transition: "all 0.3s ease",
      boxShadow: "0 4px 12px rgba(212, 175, 55, 0.2)",
    },
    searchIcon: {
      color: "#D4AF37",
      fontSize: "16px",
      marginRight: "10px",
      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
    },
    searchInput: {
      backgroundColor: "transparent",
      border: "none",
      outline: "none",
      color: "#D4AF37",
      fontSize: "14px",
      flexGrow: 1,
      padding: "0",
      width: "calc(100% - 40px)",
      fontWeight: "500",
    },
    filterContainer: {
      display: "flex",
      gap: "15px",
      flexWrap: "wrap",
      justifyContent: isSmallMobile ? "center" : "flex-start",
      width: isSmallMobile ? "100%" : "auto",
    },
    selectFilter: {
      width: "120px",
      backgroundColor: "#222",
      color: "#E5D0A5",
      border: "2px solid #444",
      borderRadius: "8px",
      padding: "8px 12px",
      fontSize: "12px",
      outline: "none",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='%23E5D0A5' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 10px center",
      backgroundSize: "14px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontWeight: "500",
      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
      height: "42px",
    },
    addButton: {
      backgroundColor: "#D4AF37",
      color: "#000",
      border: "none",
      borderRadius: "10px",
      padding: "8px 16px",
      fontSize: "13px",
      fontWeight: "700",
      cursor: "pointer",
      transition: "all 0.3s ease",
      whiteSpace: "nowrap",
      height: "42px",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: "6px",
      boxShadow: "0 4px 12px rgba(212, 175, 55, 0.3)",
      letterSpacing: "0.5px",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "300px",
      width: "100%",
    },
    loadingSpinner: {
      border: "4px solid rgba(229, 208, 165, 0.2)",
      borderTop: "4px solid #D4AF37",
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      animation: "spin 1s linear infinite",
      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#E5D0A5",
      animation: "fadeIn 0.6s ease-in-out",
      fontSize: "15px",
      fontWeight: "500",
    },
    productGrid: {
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${isSmallMobile ? "260px" : "300px"}, 1fr))`,
      gap: "25px",
      marginBottom: "0px",
      padding: "0",
    },
    productCard: {
      backgroundColor: "#111",
      borderRadius: "12px",
      padding: "0px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      position: "relative",
      overflow: "hidden",
      border: "1px solid #333",
      transition: "all 0.3s ease",
      cursor: "pointer",
      boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
      animation: "fadeIn 0.5s ease-in-out",
    },
    iconContainer: {
      position: "absolute",
      top: "12px",
      right: "12px",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      zIndex: 10,
    },
    actionIcon: {
      color: "#000",
      fontSize: "14px",
      cursor: "pointer",
      backgroundColor: "#E5D0A5",
      borderRadius: "50%",
      width: "36px",
      height: "36px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      boxShadow: "0px 3px 8px rgba(0,0,0,0.3)",
      transition: "all 0.3s ease",
      fontWeight: "600",
    },
    actionIconDanger: {
      backgroundColor: "#F44336",
      color: "#FFF",
    },
    productImageContainer: {
      backgroundColor: "#222",
      borderRadius: "12px 12px 0 0",
      width: "100%",
      height: isSmallMobile ? "220px" : "280px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
      position: "relative",
      cursor: "pointer",
      transition: "transform 0.3s ease",
    },
    productImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.3s ease",
    },
    productInfoSection: {
      backgroundColor: "#E5D0A5",
      width: "100%",
      padding: "20px 15px",
      boxSizing: "border-box",
      textAlign: "center",
      borderRadius: "0 0 12px 12px",
    },
    productName: {
      fontSize: isSmallMobile ? "17px" : "19px",
      fontWeight: "700",
      color: "#000",
      marginBottom: "8px",
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      letterSpacing: "0.3px",
    },
    productPrice: {
      fontSize: isSmallMobile ? "16px" : "18px",
      color: "#000",
      textAlign: "center",
      fontWeight: "700",
      marginBottom: "10px",
      textShadow: "0 1px 2px rgba(0,0,0,0.1)",
    },
    productStatus: {
      display: "inline-block",
      padding: "6px 12px",
      borderRadius: "8px",
      fontSize: "11px",
      fontWeight: "700",
      textAlign: "center",
      marginTop: "5px",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
    statusActive: {
      backgroundColor: "#8BC34A",
      color: "#000",
    },
    statusInactive: {
      backgroundColor: "#F44336",
      color: "#FFF",
    },
    outOfStock: {
      backgroundColor: "#FF9800",
      color: "#000",
    },
    paginationSection: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      padding: "25px 20px",
      boxSizing: "border-box",
      backgroundColor: "#000",
      borderRadius: "15px",
      marginTop: "35px",
      flexWrap: "wrap",
      justifyContent: isMobile ? "center" : "space-between",
      gap: "20px",
      boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
      border: "1px solid #222",
    },
    showingText: {
      fontSize: "15px",
      color: "#E5D0A5",
      fontWeight: "500",
    },
    paginationControls: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    paginationButton: {
      backgroundColor: "#E5D0A5",
      color: "#000",
      border: "2px solid #333",
      borderRadius: "8px",
      padding: "8px 12px",
      cursor: "pointer",
      fontSize: "14px",
      transition: "all 0.3s ease",
      minWidth: "35px",
      textAlign: "center",
      fontWeight: "600",
      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    },
    paginationButtonActive: {
      backgroundColor: "#D4AF37",
      color: "#000",
      border: "2px solid #D4AF37",
      boxShadow: "0 4px 12px rgba(212, 175, 55, 0.3)",
    },
    paginationText: {
      color: "#E5D0A5",
      fontSize: "14px",
      fontWeight: "500",
    },
    pageArrow: {
      color: "#E5D0A5",
      cursor: "pointer",
      fontSize: "15px",
      padding: "8px",
      borderRadius: "6px",
      transition: "all 0.3s ease",
    },
    disabledArrow: {
      color: "#555",
      cursor: "not-allowed",
    },
  }

  return (
    <div style={styles.mainContainer}>
      <style>
        {globalStyles}
        {`
          .product-card:hover {
            transform: translateY(-8px) !important;
            box-shadow: 0 12px 32px rgba(0,0,0,0.4) !important;
          }
          
          .product-card:hover .product-image {
            transform: scale(1.05) !important;
          }
          
          .action-icon:hover {
            transform: scale(1.1) !important;
            box-shadow: 0px 4px 12px rgba(0,0,0,0.4) !important;
          }
          
          .action-icon-danger:hover {
            background-color: #d32f2f !important;
          }
          
          .add-button:hover {
            background-color: #c9a227 !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4) !important;
          }
          
          .search-container:hover {
            border-color: #FFD700 !important;
            box-shadow: 0 6px 20px rgba(212, 175, 55, 0.3) !important;
          }
          
          .select-filter:hover {
            border-color: #D4AF37 !important;
            background-color: #333 !important;
          }
          
          .pagination-button:hover {
            background-color: #d0b98e !important;
            transform: translateY(-1px) !important;
          }
          
          .page-arrow:hover {
            color: #D4AF37 !important;
            background-color: rgba(212, 175, 55, 0.1) !important;
          }
        `}
      </style>
      <Toaster position="top-right" />
      <div style={styles.contentWrapper}>
        {/* Header Section */}
        <div style={styles.headerSection}>
          <h1 style={styles.pageTitle}>Products</h1>
          <p style={styles.breadcrumbs}>Home • Products • Product Grid</p>
        </div>

        {/* Combined Search Bar and Product Grid Section */}
        <div style={styles.mainContentSection}>
          {/* Search Bar and Add Button Section */}
          <div style={styles.searchAndAddSection}>
            <div className="search-container" style={styles.searchBarContainer}>
              <FaSearch style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search products..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <div style={styles.filterContainer}>
              <select
                className="select-filter"
                style={styles.selectFilter}
                value={selectedCategory}
                onChange={handleCategoryChange}
                disabled={categoriesLoading}
              >
                <option>All Categories</option>
                {categories.map((category) => (
                  <option key={category._id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
              <select
                className="select-filter"
                style={styles.selectFilter}
                value={selectedStatus}
                onChange={handleStatusChange}
              >
                <option>All Status</option>
                <option>Active</option>
                <option>Inactive</option>
                <option>Out of Stock</option>
              </select>
              <button className="add-button" style={styles.addButton} onClick={handleAddProduct}>
                <FaPlus /> Add Product
              </button>
            </div>
          </div>

          {/* Product Grid */}
          {loading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.loadingSpinner}></div>
            </div>
          ) : products.length === 0 ? (
            <div style={styles.emptyState}>No products found</div>
          ) : (
            <div style={styles.productGrid}>
              {products.map((product) => (
                <div key={product._id} className="product-card" style={styles.productCard}>
                  <div style={styles.iconContainer}>
                    <div
                      className="action-icon"
                      style={styles.actionIcon}
                      onClick={() => handleEditProduct(product._id)}
                    >
                      <FaEdit />
                    </div>
                    <div
                      className="action-icon action-icon-danger"
                      style={{ ...styles.actionIcon, ...styles.actionIconDanger }}
                      onClick={() => handleDeleteProduct(product._id)}
                    >
                      <FaTrash />
                    </div>
                  </div>
                  <div style={styles.productImageContainer} onClick={() => handleViewProduct(product._id)}>
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images[0] || "/placeholder.svg"}
                        alt={product.name}
                        className="product-image"
                        style={styles.productImage}
                      />
                    ) : (
                      <div style={{ color: "#E5D0A5", textAlign: "center", fontWeight: "500" }}>No Image Available</div>
                    )}
                  </div>
                  <div style={styles.productInfoSection}>
                    <h3 style={styles.productName}>{product.name}</h3>
                    <p style={styles.productPrice}>${product.displayPrice.toFixed(2)}</p>
                    <span
                      style={{
                        ...styles.productStatus,
                        ...getStatusStyle(product.statusDisplay),
                      }}
                    >
                      {product.statusDisplay === "active"
                        ? "Active"
                        : product.statusDisplay === "inactive"
                          ? "Inactive"
                          : "Out of Stock"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination Section */}
        {products.length > 0 && (
          <div style={styles.paginationSection}>
            <span style={styles.showingText}>
              Showing {(pagination.page - 1) * pagination.limit + 1}-
              {Math.min(pagination.page * pagination.limit, pagination.totalProducts)} of {pagination.totalProducts}{" "}
              products
            </span>
            <div style={styles.paginationControls}>
              <FaChevronLeft
                className="page-arrow"
                style={{
                  ...styles.pageArrow,
                  ...(pagination.page === 1 && styles.disabledArrow),
                }}
                onClick={() => pagination.page > 1 && handlePageChange(pagination.page - 1)}
              />
              {renderPaginationButtons()}
              <FaChevronRight
                className="page-arrow"
                style={{
                  ...styles.pageArrow,
                  ...(pagination.page === pagination.totalPages && styles.disabledArrow),
                }}
                onClick={() => pagination.page < pagination.totalPages && handlePageChange(pagination.page + 1)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            animation: "slideIn 0.3s ease-in-out",
            fontFamily: '"Segoe UI", sans-serif',
          }}
        >
          <div
            style={{
              backgroundColor: "#111",
              padding: "35px 30px",
              borderRadius: "15px",
              width: "90%",
              maxWidth: "450px",
              color: "#E5D0A5",
              textAlign: "center",
              border: "2px solid #D4AF37",
              boxShadow: "0 0 30px rgba(212, 175, 55, 0.4)",
              animation: "slideIn 0.3s ease-in-out",
            }}
          >
            <h3
              style={{
                fontSize: "15px",
                marginBottom: "18px",
                color: "#D4AF37",
                fontWeight: "700",
                letterSpacing: "0.5px",
              }}
            >
              Confirm Deletion
            </h3>
            <p
              style={{
                fontSize: "16px",
                marginBottom: "30px",
                color: "#E5D0A5",
                lineHeight: "1.5",
                fontWeight: "500",
              }}
            >
              Are you sure you want to delete this product? This action cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "18px", flexWrap: "wrap" }}>
              <button
                onClick={confirmDeletion}
                disabled={deleting}
                style={{
                  backgroundColor: deleting ? "#666" : "#F44336",
                  color: "#fff",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  cursor: deleting ? "not-allowed" : "pointer",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "15px",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                }}
              >
                {deleting ? (
                  <span
                    style={{
                      width: "18px",
                      height: "18px",
                      border: "2px solid #fff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : (
                  "Yes, Delete"
                )}
              </button>
              <button
                onClick={() => setConfirmDelete({ open: false, productId: null })}
                style={{
                  backgroundColor: "#D4AF37",
                  color: "#000",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "700",
                  fontSize: "15px",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 12px rgba(212, 175, 55, 0.3)",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
